/**
 * Capability types.
 *
 * A `Capability` is a MERGED VIEW over the discovery calls — coded tools from
 * `sdk.tools.listAvailableTools`, and skills from
 * `workspace-steering`'s `listAvailableSkills`. It is NOT a backend entity;
 * `kind` is set by which discovery call produced the entry. Both halves are
 * live: every account holds the seeded baseline catalog skills (context
 * ADR-036), so a capability list carries `skill` entries even on a workspace
 * that defines none of its own.
 */

import { SDKError } from '../errors.js';

export type CapabilityKind = 'coded' | 'skill';

export type JSONSchema = Record<string, unknown>;

export interface Capability {
  name: string;
  description: string;
  kind: CapabilityKind;
  parameters?: JSONSchema;
  mcpId?: string;
  /** Convention layout that supplied a skill: `sot`, `claude`, `cursor`, or `codex`. */
  framework?: string;
  /** `framework:name` address that reaches this skill even when shadowed. */
  qualifiedName?: string;
  /** Framework whose same-named skill wins a bare lookup; absent when this entry wins. */
  shadowedBy?: string;
  /** Empty on workspace skills; catalog skills use `shogun`. */
  namespace?: string;
}

export interface InvokeEntryRef {
  callEntryId: string;
  resultEntryId: string;
  messageIndex: number;
}

export type InvokeOutcome = {
  kind: 'coded';
  status: string;
  /**
   * Which step failed, set only on error:
   *   - "execute_failed": the tool errored or never ran — safe to re-invoke.
   *   - "persist_failed_after_execution": the tool RAN (side effects
   *     happened) but the result was not seeded — the command message
   *     renders this distinctly ("ran, result not saved").
   */
  errorKind?: 'execute_failed' | 'persist_failed_after_execution';
  /** Set only on success (errors persist nothing). */
  entryRef?: InvokeEntryRef;
  preview?: string;
  error?: string;
};

export type ArgCapture =
  | { mode: 'none' } // zero required params
  | { mode: 'tail'; param: string } // exactly one required param
  | { mode: 'form'; fields: ArgField[] }; // more than one required param

export interface ArgField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
}

/**
 * Thrown by `ChatSession.invoke(name: string)` when a bare capability name
 * matches more than one capability (a coded tool and a skill may legitimately
 * share a name). Pass the selected `Capability` object to
 * `invoke()` instead of a bare name to disambiguate.
 */
export class AmbiguousCapabilityError extends SDKError {
  readonly capabilityName: string;
  readonly matches: Capability[];

  constructor(capabilityName: string, matches: Capability[]) {
    super(
      `Capability name "${capabilityName}" is ambiguous: it matches ${matches.length} ` +
        `capabilities (kinds: ${matches.map((m) => m.kind).join(', ')}). ` +
        `Pass the selected Capability object to invoke() instead of a bare name.`,
    );
    this.name = 'AmbiguousCapabilityError';
    this.capabilityName = capabilityName;
    this.matches = matches;
  }
}

/**
 * Thrown by `ChatSession.invoke(...)` when the invoke-capability endpoint
 * rejects the invocation with 409 because a turn is already running on the
 * session (the busy gate). A distinct type so the palette renders the
 * busy state rather than a generic failure.
 */
export class SessionBusyError extends SDKError {
  readonly sessionId: string;

  constructor(sessionId: string, message?: string) {
    super(message ?? `Session ${sessionId} is busy — a turn is already running`);
    this.name = 'SessionBusyError';
    this.sessionId = sessionId;
  }
}

/**
 * Thrown by `ChatSession.invoke(...)` when the resolved capability is
 * workspace-scoped (a file/git/repo tool) but the session is workspace-less
 * (`ChatSession.workspaceId` is undefined). Thrown SYNCHRONOUSLY, before any
 * network call — a workspace-scoped capability can never run at account scope,
 * so there is nothing to send. In practice such a capability is filtered out of
 * a workspace-less session's discovery set server-side, so this is a defensive
 * guard. A distinct type so the palette renders the
 * "needs a workspace" state rather than a generic failure.
 */
export class WorkspaceRequiredError extends SDKError {
  readonly capabilityName: string;

  constructor(capabilityName: string, message?: string) {
    super(
      message ??
        `Capability "${capabilityName}" requires a workspace, but this session is workspace-less`,
    );
    this.name = 'WorkspaceRequiredError';
    this.capabilityName = capabilityName;
  }
}
